import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font } from '@react-pdf/renderer';
import moment from 'moment';

Font.register({
    family: 'Roboto',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf'
});

Font.register({
    family: 'Roboto Bold',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
    fontWeight: 'bold',
});

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        padding: 40,
        fontFamily: 'Roboto',
        fontSize: 10,
        color: '#333'
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 40,
        borderBottom: '1px solid #ECE4E4',
        paddingBottom: 20
    },
    title: {
        fontSize: 24,
        fontFamily: 'Roboto Bold',
        color: '#5C1628'
    },
    detailsSection: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 40
    },
    column: {
        flexDirection: 'column',
        width: '45%'
    },
    sectionTitle: {
        fontSize: 12,
        fontFamily: 'Roboto Bold',
        marginBottom: 8,
        color: '#555'
    },
    text: {
        marginBottom: 4,
        lineHeight: 1.4
    },
    bold: {
        fontFamily: 'Roboto Bold',
    },
    table: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginTop: 20,
        borderTop: '1px solid #ECE4E4',
    },
    tableHeaderRow: {
        flexDirection: 'row',
        borderBottom: '1px solid #ECE4E4',
        paddingVertical: 8,
        backgroundColor: '#F8F5F4'
    },
    tableRow: {
        flexDirection: 'row',
        borderBottom: '1px solid #ECE4E4',
        paddingVertical: 12
    },
    col1: { width: '50%', paddingLeft: 8 },
    col2: { width: '15%', textAlign: 'center' },
    col3: { width: '15%', textAlign: 'right' },
    col4: { width: '20%', textAlign: 'right', paddingRight: 8 },
    totals: {
        marginTop: 20,
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
    totalsBox: {
        width: '35%',
        flexDirection: 'column'
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 4
    },
    grandTotal: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 8,
        paddingTop: 8,
        borderTop: '1px solid #ECE4E4',
        fontFamily: 'Roboto Bold',
        fontSize: 12
    },
    footer: {
        position: 'absolute',
        bottom: 40,
        left: 40,
        right: 40,
        textAlign: 'center',
        color: '#999',
        fontSize: 8,
        borderTop: '1px solid #ECE4E4',
        paddingTop: 10
    }
});

const InvoicePdf = ({ participant, eventName, eventData, authorSetup }) => {
    // Generate an invoice number based on participant ID and date
    const invoiceNo = `FVS/${moment().format('YYYY/MM')}/${participant?.participantId || '001'}`;
    const invoiceDate = moment().format('DD.MM.YYYY');
    
    // Parse invoice data seamlessly (JSON vs Plain Text vs Nested)
    let parsedData = {};
    let rawString = '';
    if (participant?.invoiceData) {
        if (typeof participant.invoiceData === 'object') {
            parsedData = participant.invoiceData;
            rawString = JSON.stringify(parsedData);
        } else if (typeof participant.invoiceData === 'string') {
            rawString = participant.invoiceData;
            try {
                parsedData = JSON.parse(participant.invoiceData);
            } catch (e) {
                console.error('Failed to parse invoiceData as JSON, treating as plain string');
            }
        }
    }

    // Flat out potential nested objects
    const flatData = {
        ...parsedData,
        ...(typeof parsedData.invoice === 'object' ? parsedData.invoice : {}),
        ...(typeof parsedData.company === 'object' ? parsedData.company : {}),
        ...(typeof parsedData.buyer === 'object' ? parsedData.buyer : {})
    };

    // Helper to find key case-insensitively
    const findValue = (keys) => {
        const key = Object.keys(flatData).find(k => keys.includes(k.toLowerCase()));
        return key ? flatData[key] : null;
    };

    // Wyciągamy dane z różnych możliwych formatów/kluczy
    const nip = findValue(['nip', 'nipnumber', 'taxid', 'vatnumber']);
    const companyName = findValue(['companyname', 'company', 'nazwa_firmy', 'firm', 'nazwafirmy', 'nazwafirmy']);
    const personName = findValue(['fullname', 'name', 'imie_nazwisko', 'imienazwisko', 'imie', 'nazwisko']) || participant?.fullName;
    
    // Adres
    const streetInfo = findValue(['address', 'street', 'ulica']) || '';
    const buildingInfo = findValue(['building', 'nr_budynku']) || '';
    const aptInfo = findValue(['apartment', 'nr_lokalu']) || '';
    const address = [streetInfo, buildingInfo, aptInfo ? `/${aptInfo}` : ''].filter(Boolean).join(' ').replace(/\s+\//, '/') || '';

    const postalCode = findValue(['postalcode', 'zipcode', 'kod', 'kod_pocztowy', 'kodpocztowy']) || '';
    const city = findValue(['city', 'miasto']) || '';
    const buyerPostal = `${postalCode} ${city}`.trim();

    // Typ nabywcy
    const invoiceType = findValue(['type', 'invoicetype', 'accounttype']);
    const isExplicitCompany = invoiceType === 'business' || invoiceType === 'company' || invoiceType === 'firma';
    const isCompanyByData = !!nip || !!companyName;
    const isCompanyByString = Object.keys(flatData).length === 0 && /nip|firma|spółka|sp\./i.test(rawString);
    const isCompany = isExplicitCompany || isCompanyByData || isCompanyByString;

    // Jeżeli to czysty tekst (nie udało się rozbić na pola i brak struktury)
    const isPlainText = Object.keys(flatData).length === 0 && rawString.length > 0;

    // Buyer Information
    let buyerName = isCompany ? (companyName || personName || 'Firma') : (personName || 'Nie podano imienia');
    let buyerAddress = address;

    if (isPlainText) {
        buyerName = isCompany ? 'Firma' : personName;
        buyerAddress = rawString;
    }

    // Prices and math
    const ticketCount = participant?.bearerTicketCount || 1;
    const totalGross = Number(participant?.totalGross || 0);
    const unitGross = ticketCount > 0 ? totalGross / ticketCount : totalGross;

    // Assuming 23% VAT for placeholder (reverse math from gross)
    const totalNet = totalGross / 1.23;
    const unitNet = ticketCount > 0 ? totalNet / ticketCount : totalNet;

    // Seller (Wystawca) Information
    let sellerName = eventData?.authorFullName || eventData?.host?.name || 'Brak danych sprzedawcy';
    let sellerAddress = eventData?.authorAddress || '';
    let sellerTaxInfo = [];

    // Dane konta autora (setup)
    if (authorSetup) {
        // Priorytet mają dedykowane dane do faktury, jeśli zostały uzupełnione
        const inv = authorSetup.invoice || {};
        const isInvoiceSetup = !!(inv.companyName || inv.nip || inv.NIP);
        const config = isInvoiceSetup ? inv : authorSetup;

        if (authorSetup.accountType === 'business' || isInvoiceSetup) {
            sellerName = config.companyName || sellerName;
        } else {
            sellerName = (authorSetup.firstName || authorSetup.lastName) 
                ? `${authorSetup.firstName || ''} ${authorSetup.lastName || ''}`.trim() 
                : sellerName;
        }

        const street = config.street || '';
        const building = config.building || '';
        const apartment = config.apartment ? `/${config.apartment}` : '';
        const postal = config.postalCode || '';
        const city = config.city || '';
        
        const generatedAddress = `${street} ${building}${apartment}, ${postal} ${city}`.replace(/^[\s,]+/, '').trim();
        if (generatedAddress && generatedAddress.length > 5) {
            sellerAddress = generatedAddress;
        }

        const nip = config.nip || config.NIP;
        const regon = config.regon || config.REGON;
        const krs = config.krs || config.KRS;

        if (nip) sellerTaxInfo.push(`NIP: ${nip}`);
        if (regon) sellerTaxInfo.push(`REGON: ${regon}`);
        if (krs) sellerTaxInfo.push(`KRS: ${krs}`);
    }

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <View>
                        <Text style={styles.title}>FAKTURA VAT</Text>
                        <Text style={styles.text}>Nr {invoiceNo}</Text>
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                        <Text style={styles.text}>Data wystawienia: {invoiceDate}</Text>
                        <Text style={styles.text}>Data sprzedaży: {moment(participant?.registeredAt).format('DD.MM.YYYY')}</Text>
                    </View>
                </View>

                <View style={styles.detailsSection}>
                    <View style={styles.column}>
                        <Text style={styles.sectionTitle}>Sprzedawca (Wystawca)</Text>
                        <Text style={styles.bold}>{sellerName}</Text>
                        {sellerAddress ? <Text style={styles.text}>{sellerAddress}</Text> : null}
                        {sellerTaxInfo.map((info, idx) => (
                            <Text key={idx} style={styles.text}>{info}</Text>
                        ))}
                    </View>
                    <View style={styles.column}>
                        <Text style={styles.sectionTitle}>Nabywca {isCompany ? '(Firma)' : '(Osoba fizyczna)'}</Text>
                        <Text style={styles.bold}>{buyerName}</Text>
                        {isCompany && personName && personName !== buyerName ? (
                            <Text style={styles.text}>{personName}</Text>
                        ) : null}
                        {buyerAddress ? <Text style={styles.text}>{buyerAddress}</Text> : null}
                        {buyerPostal ? <Text style={styles.text}>{buyerPostal}</Text> : null}
                        {isCompany && nip ? <Text style={styles.text}>NIP: {nip}</Text> : null}
                    </View>
                </View>

                <View style={styles.table}>
                    <View style={styles.tableHeaderRow}>
                        <Text style={[styles.col1, styles.bold]}>Nazwa usługi / Wydarzenie</Text>
                        <Text style={[styles.col2, styles.bold]}>Ilość</Text>
                        <Text style={[styles.col3, styles.bold]}>Cena jedn. brutto</Text>
                        <Text style={[styles.col4, styles.bold]}>Wartość brutto</Text>
                    </View>
                    
                    <View style={styles.tableRow}>
                        <View style={styles.col1}>
                            <Text>{eventName || 'Wydarzenie Evenado'}</Text>
                            <Text style={{ fontSize: 8, color: '#666', marginTop: 4 }}>Bilety: {participant?.ticketTypeName || 'Standard'}</Text>
                        </View>
                        <Text style={styles.col2}>{ticketCount}</Text>
                        <Text style={styles.col3}>{unitGross.toFixed(2)} PLN</Text>
                        <Text style={styles.col4}>{totalGross.toFixed(2)} PLN</Text>
                    </View>
                </View>

                <View style={styles.totals}>
                    <View style={styles.totalsBox}>
                        <View style={styles.totalRow}>
                            <Text>Razem Netto:</Text>
                            <Text>{totalNet.toFixed(2)} PLN</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text>Kwota VAT (23%):</Text>
                            <Text>{(totalGross - totalNet).toFixed(2)} PLN</Text>
                        </View>
                        <View style={styles.grandTotal}>
                            <Text>Do zapłaty Brutto:</Text>
                            <Text>{totalGross.toFixed(2)} PLN</Text>
                        </View>
                    </View>
                </View>

                <View style={{ marginTop: 40 }}>
                    <Text style={styles.text}>Sposób płatności: Zapłacono z góry (Przelew online)</Text>
                </View>

                <View style={styles.footer}>
                    <Text>Faktura wygenerowana automatycznie przez system Evenado. Nie wymaga podpisu.</Text>
                </View>
            </Page>
        </Document>
    );
};

export default InvoicePdf;
